import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaStar, FaHeart, FaRegHeart } from "react-icons/fa";
import ReviewUserImage from "../../img/icons/ReviewUserImage.png";
import PurplArrowPrev from "../../img/icons/PurplArrowPrev.png";
import PurplArrowNext from "../../img/icons/PurplArrowNext.png";
import "./reviews.css";
import { UserContext } from "../../context/UserContext";
import { toast } from "react-toastify";
import useFlag from "../../hooks/useFlag";
import FlagButton from "../../components/FlagButton";
import FlagModal from "../../components/FlagModal";

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const RATING_OPTIONS = [
  { label: "All Ratings", value: "" },
  { label: "5 Stars", value: "5" },
  { label: "4+ Stars", value: "4" },
  { label: "3+ Stars", value: "3" },
  { label: "2+ Stars", value: "2" },
];

const SORT_OPTIONS = [
  { label: "Newest First", value: "newest" },
  { label: "Oldest First", value: "oldest" },
  { label: "Highest Rated", value: "highest" },
  { label: "Most Liked", value: "mostLiked" },
];

const MONTH_OPTIONS = [
  { label: "All Months", value: "" },
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

// Suggested tags (dance styles + common tags)
const SUGGESTED_TAGS = [
  'Salsa', 'Bachata', 'Kizomba', 'Zouk', 'West Coast Swing',
  'Tango', 'Ballroom', 'Latin', 'Hip Hop', 'Contemporary',
  'Beginner Friendly', 'Social Dancing', 'Workshops', 'Live Music',
  'International', 'Beach', 'Indoor', 'Outdoor'
];

const MAX_TAGS = 7;

const CATEGORY_TOOLTIPS = {
  venue: "Quality of the dance floor, air circulation, acoustics, lighting, and overall atmosphere. Was it spacious and comfortable?",
  ratio: "Balance between leads and follows. Were there taxi dancers? Were you able to find dance partners easily throughout the event?",
  org: "Event logistics, schedule, communication, registration process, and staff helpfulness.",
  artists: "Were DJs and Instructors able to create a rich & consistently enjoyable experience for the attendees?",
  culture: "Did it feel like a friendly, inclusive, and respectful community? Were those values encouraged? Were there discussions on dance roots and etiquette?",
  safety: "Did it feel like a secure and safe environment? Were there materials or workshops on consent awareness; appointed harassment prevention team members?",
};


export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [likedReviews, setLikedReviews] = useState({});
  const { user } = useContext(UserContext);

  const {
    isModalOpen: isFlagModalOpen,
    isSubmitting: isFlagSubmitting,
    alreadyFlagged,
    flagReasons,
    openFlagModal,
    closeFlagModal,
    submitFlag,
    isFlagged,
  } = useFlag({
    contentType: 'review',
    isLoggedIn: () => !!user,
    onAuthRequired: () => toast.info('Please log in to flag reviews'),
    onError: (msg) => toast.error(msg),
    onSuccess: (contentId, wasAutoHidden) => {
      if (wasAutoHidden) {
        toast.success('Thank you for your report. This review has been automatically hidden for moderation.');
      } else {
        toast.success('Review flagged. Our team will review it.');
      }
    },
  });

  // Filters
  const [minRating, setMinRating] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [monthFilter, setMonthFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [styleSearch, setStyleSearch] = useState("");
  const [showStyleSuggestions, setShowStyleSuggestions] = useState(false);

  const handleLikeReview = async (reviewId, e) => {
    if (e) e.stopPropagation();

    if (!user) {
      toast.info("Please log in to like reviews");
      return;
    }

    const token = localStorage.getItem('token');
    const isCurrentlyLiked = likedReviews[reviewId];

    try {
      await axios.put(
        `${API_BASE}/events/like-review/${reviewId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLikedReviews(prev => ({
        ...prev,
        [reviewId]: !isCurrentlyLiked
      }));

      setReviews(prev => prev.map(rev => {
        if (rev._id === reviewId) {
          return {
            ...rev,
            likeCount: isCurrentlyLiked ? (rev.likeCount || 1) - 1 : (rev.likeCount || 0) + 1
          };
        }
        return rev;
      }));

      // Also update selectedReview if it's the same
      if (selectedReview && selectedReview._id === reviewId) {
        setSelectedReview(prev => ({
          ...prev,
          likeCount: isCurrentlyLiked ? (prev.likeCount || 1) - 1 : (prev.likeCount || 0) + 1
        }));
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to like review");
      }
    }
  };

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 6,
        sortBy,
      });

      if (minRating) params.append("minRating", minRating);
      if (locationFilter.trim()) params.append("location", locationFilter.trim());

      const url = `${API_BASE}/events/feedback?${params}`;
      const response = await axios.get(url);
      setReviews(response.data.data || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, minRating, sortBy, locationFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [minRating, sortBy, monthFilter, selectedStyles, locationFilter]);

  // Filter suggestions based on search input
  const tagSuggestions = styleSearch.trim()
    ? SUGGESTED_TAGS.filter(tag =>
        tag.toLowerCase().includes(styleSearch.toLowerCase()) &&
        !selectedStyles.includes(tag)
      )
    : SUGGESTED_TAGS.filter(tag => !selectedStyles.includes(tag));

  // Check if we can add more tags
  const canAddMoreTags = selectedStyles.length < MAX_TAGS;

  // Add a tag from suggestions or custom input
  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !selectedStyles.includes(trimmedTag) && canAddMoreTags) {
      setSelectedStyles(prev => [...prev, trimmedTag]);
    }
    setStyleSearch("");
    setShowStyleSuggestions(false);
  };

  // Handle Enter key for custom tag input
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && styleSearch.trim() && canAddMoreTags) {
      e.preventDefault();
      addTag(styleSearch);
    }
  };

  // Remove a selected tag
  const removeTag = (tag) => {
    setSelectedStyles(prev => prev.filter(s => s !== tag));
  };

  // Check if any filters are active
  const hasActiveFilters = selectedStyles.length > 0 || minRating || monthFilter || locationFilter || sortBy !== "newest";

  // Clear all filters
  const clearFilters = () => {
    setMinRating("");
    setMonthFilter("");
    setLocationFilter("");
    setSelectedStyles([]);
    setSortBy("newest");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    for (let i = 0; i < 5; i++) {
      stars.push(
        <FaStar
          key={i}
          className={i < fullStars ? "star-filled" : "star-empty"}
        />
      );
    }
    return stars;
  };

  const truncateWords = (text, maxWords = 30) => {
    if (!text) return { text: "", isTruncated: false };
    const words = text.split(/\s+/);
    if (words.length <= maxWords) {
      return { text, isTruncated: false };
    }
    return { text: words.slice(0, maxWords).join(" ") + "...", isTruncated: true };
  };

  // Filter reviews by selected styles
  const filteredReviews = reviews.filter(review => {
    if (selectedStyles.length === 0) return true;

    const eventStyles = [
      ...(review.event?.danceStyles || []),
      ...(review.event?.category || [])
    ];
    return selectedStyles.some(style =>
      eventStyles.some(es => es.toLowerCase().includes(style.toLowerCase()))
    );
  });

  return (
    <main className="reviews-page">
      {/* Hero Section */}
      <section className="reviews-hero">
        <div className="container">
          <h1 className="reviews-hero-title">Discover Events Through <span className="highlight-yellow">Reviews</span></h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="reviews-content">
        <div className="container">
          {/* Filter Bar */}
          <div className="reviews-filter-bar">
            <span className="reviews-count">
              {filteredReviews.length} Review{filteredReviews.length !== 1 ? 's' : ''}
            </span>

            <div className="reviews-filters-inline">
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="reviews-select-sm"
              >
                {MONTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                className="reviews-select-sm"
              >
                {RATING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="reviews-select-sm"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <input
                type="text"
                className="reviews-select-sm filter-input"
                placeholder="City or Country"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              />

              {/* Add Filter Input */}
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  className="reviews-select-sm filter-input"
                  placeholder={canAddMoreTags ? "+ Add filter" : "Max 7 tags"}
                  value={styleSearch}
                  onChange={(e) => setStyleSearch(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => canAddMoreTags && setShowStyleSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowStyleSuggestions(false), 200)}
                  disabled={!canAddMoreTags}
                />
                {showStyleSuggestions && tagSuggestions.length > 0 && (
                  <div className="filter-dropdown">
                    {tagSuggestions.slice(0, 8).map((tag) => (
                      <button
                        key={tag}
                        className="filter-dropdown-item"
                        onClick={() => addTag(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {hasActiveFilters && (
                <button
                  className="reviews-clear-filters"
                  onClick={clearFilters}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Selected Tags Row - Below Separator */}
          {selectedStyles.length > 0 && (
            <div className="reviews-tags-row">
              {selectedStyles.map((tag) => (
                <span key={tag} className="filter-tag">
                  {tag}
                  <button className="filter-tag-remove" onClick={() => removeTag(tag)}>×</button>
                </span>
              ))}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="reviews-loading">
              <div className="spinner-border text-secondary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading reviews...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredReviews.length === 0 && (
            <div className="reviews-empty">
              <div className="reviews-empty-icon">
                <FaStar />
              </div>
              <h3>No reviews found</h3>
              <p>
                {selectedStyles.length > 0
                  ? "No reviews match the selected dance styles. Try removing some filters."
                  : "Try adjusting your filters or check back later for new reviews"}
              </p>
            </div>
          )}

          {/* Reviews Grid */}
          {!loading && filteredReviews.length > 0 && (
            <div className="reviews-grid-wrapper">
              <button
                className="reviews-nav-arrow"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <img src={PurplArrowPrev} alt="Previous" />
              </button>

              <div className="reviews-grid">
              {filteredReviews.map((review) => {
                const { text: previewText, isTruncated } = truncateWords(review.description, 50);
                return (
                  <article key={review._id} className="review-tile">
                    {/* Top Row: Avatar/Meta LEFT, Categories RIGHT */}
                    <div className="tile-top-row">
                      <div className="tile-header">
                        <div className="tile-avatar">
                          <img src={ReviewUserImage} alt="reviewer" />
                        </div>
                        <div className="tile-meta">
                          <span className="tile-role">{review.role || "attendee"}</span>
                          <div className="tile-stars">
                            {renderStars(review.stars || 0)}
                          </div>
                        </div>
                      </div>
                      <div className="tile-categories">
                        <span data-tooltip={CATEGORY_TOOLTIPS.venue}>Venue <strong>{review.venue}</strong></span>
                        <span data-tooltip={CATEGORY_TOOLTIPS.ratio}>Ratio <strong>{review.ratio}</strong></span>
                        <span data-tooltip={CATEGORY_TOOLTIPS.org}>Org <strong>{review.organization}</strong></span>
                        <span data-tooltip={CATEGORY_TOOLTIPS.artists}>Artists <strong>{review.artists}</strong></span>
                        <span data-tooltip={CATEGORY_TOOLTIPS.culture}>Culture <strong>{review.culture}</strong></span>
                        <span data-tooltip={CATEGORY_TOOLTIPS.safety}>Safety <strong>{review.safety}</strong></span>
                      </div>
                    </div>

                    {/* Review Title */}
                    {review.title && (
                      <h3 className="tile-title">{review.title}</h3>
                    )}

                    {/* Review Text Preview */}
                    <p className="tile-description">{previewText}</p>
                    {isTruncated && (
                      <button
                        className="tile-see-more"
                        onClick={() => setSelectedReview(review)}
                      >
                        •••
                      </button>
                    )}

                    {/* Amenity tags (left) & Like+Flag (right) for short reviews */}
                    {!isTruncated && (
                      <div className="tile-like-row">
                        {review.eventAmenities && (
                          <div className="tile-amenity-tags">
                            {review.eventAmenities.codeOfConduct && <span className="tile-amenity-tag"><i className="bi bi-shield-check"></i> Conduct</span>}
                            {review.eventAmenities.freeWater && <span className="tile-amenity-tag"><i className="bi bi-droplet"></i> Water</span>}
                            {review.eventAmenities.freeSnacks && <span className="tile-amenity-tag"><i className="bi bi-cup-hot"></i> Snacks</span>}
                            {review.eventAmenities.culturalDiscussions && <span className="tile-amenity-tag"><i className="bi bi-chat-heart"></i> Culture</span>}
                            {review.eventAmenities.switchHour && <span className="tile-amenity-tag"><i className="bi bi-arrow-repeat"></i> Switch</span>}
                            {review.eventAmenities.socialActivities && <span className="tile-amenity-tag"><i className="bi bi-people"></i> Social</span>}
                          </div>
                        )}
                        <div className="tile-actions-right">
                          <button
                            className="tile-like-btn"
                            onClick={(e) => handleLikeReview(review._id, e)}
                          >
                            {likedReviews[review._id] ? (
                              <FaHeart color="#A65474" size={16} />
                            ) : (
                              <FaRegHeart color="#888" size={16} />
                            )}
                            <span>{review.likeCount || 0}</span>
                          </button>
                          <FlagButton
                            onClick={() => openFlagModal(review._id)}
                            isFlagged={isFlagged(review._id)}
                            size="small"
                            title="Report this review"
                          />
                        </div>
                      </div>
                    )}

                    {/* Bottom Row: Event Name (left) & Location + Date (right) */}
                    <div className="tile-footer">
                      {review.event && (
                        <Link to={`/event/${review.event._id}`} className="tile-event-name">
                          {review.event.title}
                        </Link>
                      )}
                      <div className="tile-location-date">
                        {(review.event?.city || review.event?.country) && (
                          <span>
                            {[review.event.city, review.event.country].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {review.createdAt && <span>{formatDate(review.createdAt)}</span>}
                      </div>
                    </div>
                  </article>
                );
              })}
              </div>

              <button
                className="reviews-nav-arrow"
                onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
                disabled={currentPage === (pagination.totalPages || 1)}
              >
                <img src={PurplArrowNext} alt="Next" />
              </button>
            </div>
          )}

          {/* Review Modal */}
          {selectedReview && (
            <div className="review-modal-overlay" onClick={() => setSelectedReview(null)}>
              <div className="review-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setSelectedReview(null)}>×</button>

                <div className="modal-header">
                  <div className="tile-avatar">
                    <img src={ReviewUserImage} alt="reviewer" />
                  </div>
                  <div className="modal-meta">
                    <span className="tile-role">{selectedReview.role || "attendee"}</span>
                    <div className="tile-stars">
                      {renderStars(selectedReview.stars || 0)}
                    </div>
                  </div>
                  <div className="modal-categories">
                    <span data-tooltip={CATEGORY_TOOLTIPS.venue}>Venue <strong>{selectedReview.venue}</strong></span>
                    <span data-tooltip={CATEGORY_TOOLTIPS.ratio}>Ratio <strong>{selectedReview.ratio}</strong></span>
                    <span data-tooltip={CATEGORY_TOOLTIPS.org}>Org <strong>{selectedReview.organization}</strong></span>
                    <span data-tooltip={CATEGORY_TOOLTIPS.artists}>Artists <strong>{selectedReview.artists}</strong></span>
                    <span data-tooltip={CATEGORY_TOOLTIPS.culture}>Culture <strong>{selectedReview.culture}</strong></span>
                    <span data-tooltip={CATEGORY_TOOLTIPS.safety}>Safety <strong>{selectedReview.safety}</strong></span>
                  </div>
                </div>

                {selectedReview.title && (
                  <h3 className="modal-title">{selectedReview.title}</h3>
                )}

                <p className="modal-description">{selectedReview.description}</p>

                {/* Amenity tags (left) & Like+Flag (right) in modal */}
                <div className="modal-like-row">
                  {selectedReview.eventAmenities && (
                    <div className="tile-amenity-tags">
                      {selectedReview.eventAmenities.codeOfConduct && <span className="tile-amenity-tag"><i className="bi bi-shield-check"></i> Conduct</span>}
                      {selectedReview.eventAmenities.freeWater && <span className="tile-amenity-tag"><i className="bi bi-droplet"></i> Water</span>}
                      {selectedReview.eventAmenities.freeSnacks && <span className="tile-amenity-tag"><i className="bi bi-cup-hot"></i> Snacks</span>}
                      {selectedReview.eventAmenities.culturalDiscussions && <span className="tile-amenity-tag"><i className="bi bi-chat-heart"></i> Culture</span>}
                      {selectedReview.eventAmenities.switchHour && <span className="tile-amenity-tag"><i className="bi bi-arrow-repeat"></i> Switch</span>}
                      {selectedReview.eventAmenities.socialActivities && <span className="tile-amenity-tag"><i className="bi bi-people"></i> Social</span>}
                    </div>
                  )}
                  <div className="tile-actions-right">
                    <button
                      className="tile-like-btn"
                      onClick={(e) => handleLikeReview(selectedReview._id, e)}
                    >
                      {likedReviews[selectedReview._id] ? (
                        <FaHeart color="#A65474" size={18} />
                      ) : (
                        <FaRegHeart color="#888" size={18} />
                      )}
                      <span>{selectedReview.likeCount || 0}</span>
                    </button>
                    <FlagButton
                      onClick={() => openFlagModal(selectedReview._id)}
                      isFlagged={isFlagged(selectedReview._id)}
                      size="small"
                      title="Report this review"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  {selectedReview.event && (
                    <Link
                      to={`/event/${selectedReview.event._id}`}
                      className="tile-event-name"
                      onClick={() => setSelectedReview(null)}
                    >
                      {selectedReview.event.title}
                    </Link>
                  )}
                  <div className="tile-location-date">
                    {(selectedReview.event?.city || selectedReview.event?.country) && (
                      <span>
                        {[selectedReview.event.city, selectedReview.event.country].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {selectedReview.createdAt && <span>{formatDate(selectedReview.createdAt)}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Page Info */}
          {!loading && pagination.totalPages > 1 && (
            <div className="reviews-page-info">
              Page {currentPage} of {pagination.totalPages}
            </div>
          )}
        </div>
      </section>

      {/* Flag Modal */}
      <FlagModal
        isOpen={isFlagModalOpen}
        onClose={closeFlagModal}
        onSubmit={submitFlag}
        isSubmitting={isFlagSubmitting}
        alreadyFlagged={alreadyFlagged}
        flagReasons={flagReasons}
        contentType="review"
      />

      {/* CTA Section */}
      <section className="reviews-cta">
        <div className="container">
          <h2>Been to a dance event?</h2>
          <p>Share your experience and help fellow dancers find their next adventure</p>
          <Link to="/events" className="reviews-cta-btn">
            Find Past Events to Review
          </Link>
        </div>
      </section>
    </main>
  );
}
